import { useState } from 'react';
import parse, {
  domToReact,
  type Element,
  type HTMLReactParserOptions,
  type DOMNode,
} from 'html-react-parser';
import { ArrowLeft } from '@hungryroot/icons';
import { Button, Paragraph, Heading } from '@hungryroot/ramen';
import { CreditMessage } from '../../components/CreditMessage/CreditMessage';
import styles from './Confirm.module.css';
import pageStyles from '../page.module.css';

interface Props {
  testid?: string;
  cancelFreeTextHeader: string | null;
  onNext: (value: string) => void;
  onPrev: () => void;
}

export function Confirm(props: Props) {
  const { testid, cancelFreeTextHeader, onNext, onPrev } = props;
  const [value, setValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const html =
    cancelFreeTextHeader !== null && cancelFreeTextHeader !== undefined
      ? cancelFreeTextHeader
      : '';

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const node = domNode as Element;
      const children = node.children as DOMNode[];

      if (node?.name === 'h2') {
        return (
          <Heading
            variant="Hero"
            className={pageStyles.heading}
            testid={`${testid}-text-section-heading`}
          >
            {domToReact(children, options)}
          </Heading>
        );
      }
      if (node?.name === 'p') {
        return (
          <Paragraph
            variant="lead"
            testid={`${testid}-text-section-heading`}
            className={styles.body}
          >
            {domToReact(children, options)}
          </Paragraph>
        );
      }
      if (node?.name === 'a') {
        return (
          <a
            data-testid={`${testid}-contact-link`}
            className={styles.link}
            href="https://support.hungryroot.com/hc/en-us/articles/115015334547-How-do-I-contact-Hungryroot"
            target="_blank"
          >
            {domToReact(children, options)}
          </a>
        );
      }
    },
  };

  return (
    <div data-testid={testid} className={pageStyles.container}>
      <Button
        onClick={onPrev}
        style="default"
        testid={`${testid}-prev`}
        size="medium"
        prominence="tertiary"
        icon={<ArrowLeft />}
        iconPosition="left"
        textPrimary="Back"
        className={pageStyles.back}
      />
      <div
        data-testid={`${testid}-text-section`}
        className={`${pageStyles.text}`}
      >
        {parse(html, options)}
      </div>
      <CreditMessage
        testid={`${testid}-credit-message`}
        className={pageStyles.credit}
      />
      <div className={pageStyles.content}>
        <div className={pageStyles['content-inner']}>
          <Paragraph>Do you have anything else you’d like to share?</Paragraph>
          <textarea
            className={styles.textarea}
            placeholder="Type your feedback here..."
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
          />
        </div>
      </div>
      <Button
        onClick={() => {
          setIsLoading(true);
          onNext(value);
        }}
        style="critical"
        testid={`${testid}-next`}
        size="medium"
        prominence="primary"
        textPrimary="CONTINUE TO CANCEL"
        className={pageStyles.confirm}
        loading={isLoading}
      />
    </div>
  );
}
